import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const sections = [
  {
    number: '01',
    label: 'About Us',
    path: '/about',
    headline: 'A firm built on restraint.',
    body: 'We manage a deliberately small portfolio. Every property receives the full weight of our attention — not a fraction of it.',
  },
  {
    number: '02',
    label: 'Philosophy',
    path: '/philosophy',
    headline: 'The long view, always.',
    body: 'Our decisions are made with the asset\'s decade in mind, not its quarter. Stewardship over transaction.',
  },
  {
    number: '03',
    label: 'Services',
    path: '/services',
    headline: 'Every discipline, in-house.',
    body: 'Tenant screening, lease management, maintenance coordination — executed with the same standard across every engagement.',
  },
  {
    number: '04',
    label: 'Property Types',
    path: '/properties',
    headline: 'Exceptional properties only.',
    body: 'From urban penthouses to private estates. We are selective about what we manage, and that selectivity protects your asset.',
  },
  {
    number: '05',
    label: 'Our Team',
    path: '/team',
    headline: 'Known only by introduction.',
    body: 'Three principals. A combined legacy of discretion and results. Their identities are shared when the time is right.',
  },
];

function TeaserRow({
  item,
  index,
}: {
  item: (typeof sections)[0];
  index: number;
}) {
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.15 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      onClick={() => navigate(item.path)}
      className={`group relative flex flex-col md:flex-row md:items-center gap-6 md:gap-12 py-10 md:py-12 border-b border-[#E8E4DE] cursor-pointer transition-all duration-700 ease-out ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{ transitionDelay: `${index * 80}ms` }}
    >
      {/* Hover background wash */}
      <div className="absolute inset-0 bg-[#F7F4EF] opacity-0 group-hover:opacity-100 transition-opacity duration-400 rounded-sm -mx-6 md:-mx-10" />

      {/* Number */}
      <span className="relative shrink-0 text-[#C9A961]/50 text-xs font-medium tracking-[0.3em] uppercase w-10">
        {item.number}
      </span>

      {/* Label pill */}
      <span className="relative shrink-0 hidden md:inline-flex items-center border border-[#C9A961]/40 group-hover:border-[#C9A961] text-[#8B7355] text-[10px] uppercase tracking-[0.25em] font-medium px-3.5 py-1.5 rounded-full transition-colors duration-300 w-36 justify-center whitespace-nowrap">
        {item.label}
      </span>

      {/* Text block */}
      <div className="relative flex-1 min-w-0">
        <p className="text-[#1A1A1A] text-lg md:text-xl font-light leading-snug group-hover:text-[#2C2C2C] transition-colors duration-300">
          {item.headline}
        </p>
        <p className="mt-1.5 text-[#7A7570] text-sm font-light leading-relaxed max-w-lg">
          {item.body}
        </p>
      </div>

      {/* Arrow */}
      <div className="relative shrink-0 w-9 h-9 flex items-center justify-center rounded-full border border-[#E0DDD9] group-hover:border-[#C9A961] group-hover:bg-[#C9A961] transition-all duration-300 ml-auto md:ml-0">
        <i className="ri-arrow-right-line text-sm text-[#9A9A9A] group-hover:text-[#1A1A1A] transition-colors duration-300" />
      </div>
    </div>
  );
}

export default function HomepageTeaser() {
  const navigate = useNavigate();
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerVisible, setHeaderVisible] = useState(false);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setHeaderVisible(true); observer.disconnect(); } },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="w-full bg-white py-20 md:py-28 px-6 md:px-16 lg:px-24">
      <div className="max-w-5xl mx-auto">

        {/* Editorial header */}
        <div
          ref={headerRef}
          className={`flex flex-col md:flex-row md:items-end justify-between gap-8 mb-4 transition-all duration-800 ease-out ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
          }`}
        >
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-[#C9A961]" />
              <span className="text-[#C9A961] text-xs uppercase tracking-[0.3em] font-medium">
                Higher Standard Global
              </span>
            </div>
            <h2 className="text-[#1A1A1A] text-3xl md:text-4xl font-light leading-tight max-w-lg">
              Everything you need to know,<br />
              <em className="not-italic text-[#8B7355]">before we speak.</em>
            </h2>
          </div>

          <p
            className={`text-[#7A7570] text-sm font-light leading-relaxed max-w-xs md:text-right transition-all duration-800 ease-out ${
              headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
            style={{ transitionDelay: '150ms' }}
          >
            We believe informed clients make the best partners. Explore what we do and how we think — then decide if we are the right fit.
          </p>
        </div>

        {/* Hairline rule */}
        <div
          className={`w-full h-px bg-[#E8E4DE] mb-2 transition-all duration-800 ease-out ${
            headerVisible ? 'opacity-100' : 'opacity-0'
          }`}
          style={{ transitionDelay: '250ms' }}
        />

        {/* Section rows */}
        <div>
          {sections.map((item, i) => (
            <TeaserRow key={item.path} item={item} index={i} />
          ))}
        </div>

        {/* Bottom CTA */}
        <div
          className={`mt-14 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-10 border-t border-[#E8E4DE] transition-all duration-700 ease-out ${
            headerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
          style={{ transitionDelay: '500ms' }}
        >
          <p className="text-[#7A7570] text-sm font-light max-w-sm leading-relaxed">
            Ready to discuss your property? We accept new clients exclusively by referral or direct inquiry.
          </p>
          <button
            onClick={() => navigate('/contact')}
            className="inline-flex items-center gap-2.5 bg-[#1A1A1A] hover:bg-[#2C2C2C] text-white font-medium text-xs uppercase tracking-[0.2em] px-7 py-3.5 rounded-md transition-colors duration-300 cursor-pointer whitespace-nowrap shrink-0"
          >
            Begin the Conversation
            <i className="ri-arrow-right-line text-sm" />
          </button>
        </div>

      </div>
    </section>
  );
}
